/**
 * Centralised parent-child validity helpers.
 *
 * Pure functions over ARTIFACT_TYPE_CONFIG that answer:
 *   - "what kind of parent does this value represent?" (parseParentType)
 *   - "what value should I write for an artifact-typed parent?" (buildArtifactParentType)
 *   - "is this child-parent combination legal?" (isValidParent / assertValidParent)
 *   - "what types can be children of this parent?" (getValidChildTypes)
 *   - "what happens when an attachment lands on this parent?" (getAttachmentExtraction)
 *   - "can this type be merged with same-type instances?" (isMergeable)
 *
 * Consumers across backend (controllers, services, jobs) and frontend
 * (breadcrumbs, mobile pages, hooks) route through these helpers instead
 * of string-equating against parentEntityType literals.
 */
import { ARTIFACT_TYPES, ARTIFACT_TYPE_CONFIG, isValidArtifactType, } from './artifact';
/**
 * Parse a parentEntityType string into a kind + optional subtype.
 *
 * Accepts the legacy 'artifact' placeholder (subtype undefined) for
 * back-compat with in-flight Cloud Tasks payloads during the rollout
 * window. After the backfill migration completes, no DB row should
 * carry the placeholder — but transitional reads still need to handle it.
 */
export function parseParentType(s) {
    if (s === 'workspace')
        return { kind: 'workspace' };
    if (s === 'project')
        return { kind: 'project' };
    if (s === 'artifact')
        return { kind: 'artifact', subtype: undefined };
    if (s.startsWith('artifact.')) {
        const subtype = s.slice('artifact.'.length);
        if (isValidArtifactType(subtype)) {
            return { kind: 'artifact', subtype };
        }
    }
    return null;
}
/** Single construction point for the dotted-namespace artifact parent value. */
export function buildArtifactParentType(subtype) {
    return `artifact.${subtype}`;
}
/**
 * The two non-artifact "container" parent kinds — a workspace or a collection
 * (`project`). Every other parent an artifact can carry is another artifact
 * (`artifact.<subtype>`).
 *
 * This is the set that list surfaces treat as **top-level**: an artifact
 * parented to one of these shows as a first-class row (the `/artifacts` page,
 * the sidebar Recents), while an artifact parented to another artifact (a
 * note's attachment) is represented by its parent instead.
 *
 * Single source for both the `parentEntityType IN (...)` list filters AND the
 * `z.enum(...)` move/save target validators — so a future container kind can't
 * be added to one copy and silently omitted from the others (review CR-003).
 */
export const TOP_LEVEL_PARENT_ENTITY_TYPES = ['workspace', 'project'];
/**
 * Parse a wire/DB `parentEntityType` + `parentEntityId` into a `ParentRef`.
 * Returns `null` when the type is unrecognised — the caller surfaces a 400 or
 * skips the filter. The bare `'artifact'` placeholder maps to `artifactAny`.
 */
export function parseParentRef(parentEntityType, parentEntityId) {
    const parsed = parseParentType(parentEntityType);
    if (!parsed)
        return null;
    if (parsed.kind === 'workspace')
        return { kind: 'workspace', id: parentEntityId };
    if (parsed.kind === 'project')
        return { kind: 'project', id: parentEntityId };
    return parsed.subtype === undefined
        ? { kind: 'artifactAny', id: parentEntityId }
        : { kind: 'artifactSubtype', subtype: parsed.subtype, id: parentEntityId };
}
/**
 * The `parentEntityType` string a `ParentRef` filters on. `artifactAny` yields
 * the bare `'artifact'` placeholder, which the backend read layer normalises
 * to a `startsWith 'artifact.'` match across every subtype; a specific subtype
 * yields its exact `'artifact.<subtype>'` value.
 */
export function parentRefToEntityType(ref) {
    switch (ref.kind) {
        case 'workspace':
            return 'workspace';
        case 'project':
            return 'project';
        case 'artifactAny':
            return 'artifact';
        case 'artifactSubtype':
            return buildArtifactParentType(ref.subtype);
    }
}
/** Thrown when a write path attempts an invalid parent-child combination. */
export class InvalidParentError extends Error {
    childType;
    parentType;
    code = 'INVALID_PARENT_TYPE';
    constructor(childType, parentType) {
        super(`Artifact type '${childType}' cannot have a parent of type '${parentType}'`);
        this.childType = childType;
        this.parentType = parentType;
        this.name = 'InvalidParentError';
    }
}
/**
 * True if a child artifact of type `childType` is allowed to have a parent
 * of type `parentType`. Reads from ARTIFACT_TYPE_CONFIG[childType].validParentTypes.
 */
export function isValidParent(childType, parentType) {
    if (!isValidArtifactType(childType))
        return false;
    return ARTIFACT_TYPE_CONFIG[childType].validParentTypes.includes(parentType);
}
/** Throws InvalidParentError if the combination is not legal. No-op otherwise. */
export function assertValidParent(childType, parentType) {
    if (!isValidParent(childType, parentType)) {
        throw new InvalidParentError(childType, parentType);
    }
}
/**
 * Returns the extraction policy for when an attachment lands as a child of this
 * type. Returns 'none' for unknown types or when the field is absent — callers
 * should still have validated the parent type before this call.
 */
export function getAttachmentExtraction(parentSubtype) {
    if (!isValidArtifactType(parentSubtype))
        return 'none';
    return ARTIFACT_TYPE_CONFIG[parentSubtype].attachmentExtraction ?? 'none';
}
/**
 * Returns all artifact types that declare `parentSubtype` as a valid parent.
 * Derived from `validParentTypes` across all types — single source of truth,
 * no duplication with a stored child-types field.
 */
export function getValidChildTypes(parentType) {
    return ARTIFACT_TYPES.filter((child) => ARTIFACT_TYPE_CONFIG[child].validParentTypes.includes(parentType));
}
/** True if this artifact type supports being merged with same-type instances. */
export function isMergeable(type) {
    if (!type || !isValidArtifactType(type))
        return false;
    return ARTIFACT_TYPE_CONFIG[type].mergeable ?? false;
}
//# sourceMappingURL=parent-validity.js.map